"use client";
import { useAtom } from "jotai";
import SVG from "react-inlinesvg";
import Image from "next/image";
import { useParams } from "next/navigation";
import NewsList from "@/components/utils/NewsList";
import Accordion from "@/components/utils/Accordion";
import InViewAnimation from "@/components/utils/InViewAnimation";
import Link from "next/link";

const farmarsList = [{
  address: "北海道",
  famarList: [
    {
      address: "北海道ニセコ町",
      farmarName: "LaLaLa Farm",
      name: "服部 吉弘",
      description: "羊蹄山の麓、発酵の法則に沿って自然循環栽培<br>有機JAS<br>フルーツトマト、とうもろこし等",
      link: "https://home.tsuku2.jp/storeDetail.php?scd=0000105062"
    },
    {
      address: "北海道赤井川村",
      farmarName: "コロポックル村",
      name: "赤木 陽介",
      description: "山々に囲まれた小さな赤井川村で、科学的アプローチによる有機栽培<br>有機JAS、JGAP<br>グリーンアスパラガス、ホワイトアスパラガス",
      link: "https://koropokkurumura.com/"
    },
    {
      address: "北海道幕別町",
      farmarName: "折笠農場",
      name: "折笠 健",
      description: "創業116年、33haの大規模な畑で自然栽培<br>有機JAS<br>さやあかね（じゃがいも）",
      link: "https://orikasa-farm.jp/"
    }
  ]
},{
  address: "新潟県",
  famarList: [
    {
      address: "新潟県十日町市",
      farmarName: "柳農産",
      name: "柳 恵一",
      description: "枝豆栽培20年以上。全国えだまめ選手権最高金賞<br>枝豆",
      link: "https://tsumari-chamame.com/"
    }
  ]
},{
  address: "千葉県",
  famarList: [
    {
      address: "千葉県山武市",
      farmarName: "たがやす倶楽部",
      name: "齊藤 完一",
      description: "地域資源を活かした自家製の植物性堆肥による土づくりを40年<br>農薬・化学肥料不使用<br>にんじん（ひとみ五寸）、かぶ（ハクレイ）等",
      link: "https://www.tagayasuclub.com/"
    }
  ]
},{
  address: "山梨県",
  famarList: [
    {
      address: "山梨県北杜市",
      farmarName: "畑山農場",
      name: "畑山 貴宏",
      description: "新規就農22年。米糠や落ち葉など植物性を中心とした堆肥を用いた有機栽培<br>有機JAS<br>スナックエンドウ、ほうれん草、大根等",
      link: "https://hatayamaorganicfarm.mystrikingly.com/"
    }
  ]
},{
  address: "長野県",
  famarList: [
    {
      address: "長野県佐久穂町",
      farmarName: "のらくら農場",
      name: "萩原 紀行",
      description: "信州北八ヶ岳の畑で、土を分析し、野菜を観察する。おいしさや栄養価を狙ってつくる<br>有機JAS<br>小松菜、ケール、春菊、水菜等",
      link: "https://www.norakuranoujyou.com/"
    },
    {
      address: "長野県中川村 ",
      farmarName: "はっぱ屋",
      name: "中村 健志",
      description: "標高630mの寒暖の差を活かし、落ち葉や野菜の端材などを土へ循環させる土づくり<br>有機JAS<br>ほうれん草、小松菜、レタス、キャベツ等", 
      link: "https://happaya-farm.com/"
    }
  ]
},{
  address: "長崎県",
  famarList: [
    {
      address: "長崎県雲仙市",
      farmarName: "岩崎農園",
      name: "岩崎 政利",
      description: "自家採種による伝統野菜・在来種を育て続けて40年<br>黒田五寸人参、雲仙こぶ高菜等",
      link: null
    }
  ]
}]

export default function PageComponent({posts}) {
  const params = useParams();
  return (
    <main className="page page-vegetables">
      <div className="container-m">
        
        <InViewAnimation>
          <div className="page-title-holder">
            <h1 className="h1-title">
              <span className="shoulder">食べる革命</span>
              <span className="title en">Next Green <br className="spOnly"/>Vegetables</span>
            </h1>
            <picture className="page-title-pic">
              <video src="/assets/video/ngv.mp4" loop autoPlay muted playsInline />
              <span className="badge"><img src="/assets/img/top/badge-vegetables.svg" alt="Next GREEN Vegetables" /></span>
            </picture>
            <div className="text-lead bold">
              土づくりから生まれる<br />
              「おいしい理由」を可視化した<br className="spOnly" />日本初の野菜ブランド
            </div>
          </div> 

          <div className="separator-asterisk en">*</div>

          <section className="section section-1">
            <div className="section-title-holder">
              <h2 className="section-title bold">
                土からおいしい野菜へ
              </h2>
            </div>

            <div className="block">
              <picture className="pic round">
                <source srcSet="/assets/img/vegetables/section-1-pic.jpg" media="(min-width: 1024px)" />
                <source srcSet="/assets/img/vegetables/section-1-pic-sp.jpg" media="(max-width: 1024px)" />
                <img src="/assets/img/vegetables/section-1-pic.jpg" loading="lazy" alt="" />
              </picture>
              <div className="block-lead bold">
                自然界の多様な生態系に近い「いい土」で育った、おいしく、健康な野菜。それがネクストグリーンベジタブル。日本で初めての「おいしい理由が見える野菜」です。<br />
                これからの野菜は土で選ぼう。
              </div>
            </div>
          </section>
        </InViewAnimation>

        <div className="separator-asterisk en">*</div>

        <section className="section section-2">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              おいしい理由
            </h2>
            <div className="section-lead bold">
              ネクストグリーンから生まれる野菜のおいしさには明確な理由があります。<br className="spOnly" />それは「土づくり」と「届け方」。
            </div>
          </div>

          <div className="block n1 sticky-block-container grid-3-col">
            <div className="sticky-item block-title-holder">
              <h2 className="block-title bold">
                1. おいしく育つ土づくり
              </h2>
              <div className="block-lead bold">
                自然な生物多様性に近い「いい土」を数値で示す仕組み：Agri LCA+によって、おいしさの理由がデータで可視化されている野菜のみ取り扱っています。だから、おいしい。
              </div>
            </div>
            <div className="static-item">
              <picture className="pic sp-wide">
                <source srcSet="/assets/img/vegetables/section-2-pic.png" media="(min-width: 1024px)" />
                <source srcSet="/assets/img/vegetables/section-2-pic-sp.png" media="(max-width: 1024px)" />
                <img src="/assets/img/vegetables/section-2-pic.png" alt="" loading="lazy" />
              </picture>
            </div>
          </div>

          <div className="block n2 sticky-block-container grid-3-col">
            <div className="sticky-item block-title-holder">
              <h2 className="block-title bold">
                2.おいしく届けるこだわり
              </h2>
              <div className="block-lead bold">
                生活者のみなさんに野菜が届く。<br />
                その「届け方」の工夫をとことん追求することで、ひとつひとつの野菜が、それぞれいちばん食べておいしいときに、食卓に届くようにしました。だから、おいしい。
              </div>
            </div>
            <div className="static-item accordion-contianer-column col-2">
              <div className="accordion-contianer-wrap pcOnly">
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    品種のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      おいしさを重視した品種を選びます。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    旬のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      その野菜に適した季節に育ったものを選びます。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    出荷のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      採れたてがおいしい野菜は早く、熟すとおいしい野菜は貯蔵する。いちばんおいしいときにお届けします。
                    </div>
                  </div>
                </Accordion>
              </div>
              <div className="accordion-contianer-wrap pcOnly">
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    適地適作のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      気候や土質など、風土に合った地域で育った野菜を選びます。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    収穫のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      流通効率を優先せず、大きさや熟度など、「おいしさ」が際立つ時期に収穫します。
                    </div>
                  </div>
                </Accordion>
              </div>
              <div className="accordion-contianer-wrap spOnly">
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    品種のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      おいしさを重視した品種を選びます。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    適地適作のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      気候や土質など、風土に合った地域で育った野菜を選びます。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    旬のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      その野菜に適した季節に育ったものを選びます。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    収穫のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      流通効率を優先せず、大きさや熟度など、「おいしさ」が際立つ時期に収穫します。
                    </div>
                  </div>
                </Accordion>
                <Accordion open={true} disabled={true}>
                  <div className="accordion-title bold">
                    出荷のこだわり
                    <i className="accordion-icon" />
                  </div>
                  <div className="accordion-content">
                    <div className="accordion-text">
                      採れたてがおいしい野菜は早く、熟すとおいしい野菜は貯蔵する。いちばんおいしいときにお届けします。
                    </div>
                  </div>
                </Accordion>
              </div>
            </div>
          </div>
        </section>

        <div className="separator-asterisk en">*</div>

        <section className="section section-3">
          <div className="section-title-holder">
            <h2 className="section-title bold">
              全国各地のNext Green <br className="spOnly"/>Vegetables
            </h2>
            <div className="section-lead bold">
              「いい土」で育った、おいしく、健康な野菜を食卓へお届けするとともに、Next Green Methodによって、土からおいしい野菜を育て、広げていきます。
            </div>
          </div>

          <div className="block">
            <h2 className="block-title bold">
              全国のおいしい農家たち
            </h2>
            <div className="block-famar">
              {farmarsList.map((farmar,i) => (
                <div className="block-famar-item" key={i}>
                  <div className="item-address bold">{farmar.address}</div>
                  <ul className="famar-list">
                    {farmar.famarList.map((item,index) => (
                      <li className="famar-list-item" key={index}>
                        {item.link ? (
                          <a className="farmar-name bold scaleHover opacity" href={`${item.link}`} target="_blank" rel="noopener noreferrer">
                            <div className="inner">
                              {item.farmarName}
                              <i className="icon">
                                <img src="/assets/img/common/icon/icon-outerlink.svg" alt="" />
                              </i>
                            </div>
                          </a>
                        ):(
                          <div className="farmar-name bold">
                            <div className="inner">
                              {item.farmarName}
                            </div>
                          </div>
                        )}
                        <div className="famar-list-item-inner">
                          <div className="pic-famar">
                            <img src={`/assets/img/vegetables/famar/pic-${i+1}-${index+1}.jpg`} alt="" loading="lazy" />
                          </div>
                          <div className="famar-description">
                            <div className="address bold">{item.address}</div>
                            <div className="name bold">{item.name}さん</div>
                            <div className="description" dangerouslySetInnerHTML={{ __html: item.description }} />
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-gallery">
          <div className="pic-block n1 left">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/vegetables/gallery/pic-1.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                土からおいしい野菜を、いちばんおいしいときに、食卓へお届けします。
              </div>
            </InViewAnimation>
          </div>
          <div className="pic-block n2 right">
            <InViewAnimation>
              <div className="pic round">
                <img src="/assets/img/vegetables/gallery/pic-2.jpg" alt="" loading="lazy" />
              </div>
              <div className="pic-text">
                北海道の赤井川村コロポックル村。「いい土」で育った、おいしく、健康な野菜は全国各地から。
              </div>
            </InViewAnimation>
          </div>
        </section>

        <section className="section section-line">
          <div className="block">
            <div className="page-title-pic">
              <img src="/assets/img/top/badge-vegetables.svg" alt="Next GREEN Vegetables" />
            </div>
            <div className="text-holder">
              <div className="text bold">
                ネクストグリーンベジタブルは、2025年秋に正式にリリース予定です。<br />
                新しい情報はLINEにてご連絡しますので、ぜひ公式アカウントにご登録ください。
              </div>
              <div className="qr-holder">
                <div className="pic-qr">
                  <img src="/assets/img/vegetables/pic-qr.svg" />
                </div>
                <a className="qr-text bold scaleHover opacity" href="https://line.me/R/ti/p/@096ssbeb" target="_blank" rel="noopener noreferrer">
                  <span className="inner">
                    公式LINEから<br />
                    最新情報を受け取る
                  </span>
                </a>
              </div>
            </div>
          </div>
        </section>
        
        {posts.length > 0 && (
          <>
            <div className="separator-asterisk en">*</div>
          
            <section className="section section-method-latestcase">
              <div className="section-title-holder">
                <h2 className="section-title bold">
                  最新事例
                </h2>
              </div>
              <div className="container-xs grayCard">
                <NewsList posts={posts} />
                <Link href="/news/implementation/next_green_vegetables/" className="button-link button-round scaleHover size-s">
                  <span className="inner">すべての事例をみる</span>
                </Link>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}
